import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import HelpText from '../../components/gui/helpText';

// «Finn produktbilder»: candidates come from the part url (fast) or from a
// background Claude web search (20-90 s, polled). Only the photos the user
// picks are downloaded and stored. Spec:
// docs/superpowers/specs/2026-10-16-part-product-photo-search-design.md
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 180000;
const PENDING_TTL_MS = 10 * 60 * 1000;
const REUSE_WINDOW_MS = 24 * 60 * 60 * 1000;
const MAX_ADD_IMAGES = 6;

const SOURCE_LABELS = {
    'product-json-ld': <Text id='part.photo-search-source-product'>Product data</Text>,
    'page-image': <Text id='part.photo-search-source-page'>Page image</Text>,
};

function sourceLabel(source) {
    return SOURCE_LABELS[source] || <Text id='part.photo-search-source-meta'>Share image</Text>;
}

function hostnameOf(url) {
    try {
        return new URL(url).hostname;
    } catch (err) {
        return url || '';
    }
}

function ageOf(entry, field) {
    const value = entry && entry[field];
    return value ? Date.now() - new Date(value).getTime() : Infinity;
}

@observer
class PartPhotoSearch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            phase: 'idle', // idle | loading | pending | candidates | failed | adding
            mode: '',
            uuidv4: null,
            pageUrl: '',
            pageTitle: '',
            candidates: [],
            hidden: {},
            selected: {},
            error: null, // string | 'limit' | 'running' | 'failed'
            canUseAi: false,
            addErrors: [],
            remainingToday: null,
            pollSlow: false,
        };
        this.pollTimer = null;
        this.pollStartedAt = 0;
        this.unmounted = false;
        // The last POST body, so «Søk på nytt» after a failure repeats the same
        // (possibly free) request instead of forcing the paid AI path.
        this.lastBody = null;
    }

    componentDidMount() {
        if (!this.resumeFromStore()) {
            this.search({});
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopPolling();
    }

    // The store's part is only ours when the ids match: nested part drawers
    // load other parts into the same store.
    isStorePart = () => {
        const { id } = this.props;
        const { partStore } = this.props.stores;
        const part = partStore?.part;
        return !!part && Number(part.id) > 0 && Number(part.id) === Number(id);
    }

    storedSearches = () => {
        const { partStore } = this.props.stores;
        if (!this.isStorePart() || !Array.isArray(partStore.part.photoSearches)) {
            return [];
        }
        return partStore.part.photoSearches;
    }

    // A search started earlier (drawer closed, page reloaded) is reused instead
    // of paying for a new one: poll a fresh pending entry, show a fresh result.
    resumeFromStore = () => {
        const entries = this.storedSearches();
        const pending = entries.find(e => e && e.status === 'pending' && ageOf(e, 'createdDate') < PENDING_TTL_MS);
        if (pending) {
            this.startPolling(pending.uuidv4, new Date(pending.createdDate).getTime());
            return true;
        }
        const completed = [...entries]
            .filter(e => e && e.status === 'completed' && Array.isArray(e.candidates) && e.candidates.length > 0
                && ageOf(e, 'completedDate') < REUSE_WINDOW_MS)
            .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))[0];
        if (completed) {
            this.showCandidates({ mode: 'ai', ...completed }, { resumed: true });
            return true;
        }
        return false;
    }

    showCandidates = (data, { resumed = false } = {}) => {
        this.setState({
            phase: 'candidates',
            mode: data.mode || '',
            uuidv4: null,
            pageUrl: data.pageUrl || '',
            pageTitle: data.pageTitle || '',
            candidates: Array.isArray(data.candidates) ? data.candidates : [],
            hidden: {},
            selected: {},
            error: null,
            canUseAi: data.mode === 'url',
            addErrors: [],
            resumed,
        });
    }

    search = async body => {
        const { id } = this.props;
        const { partStore } = this.props.stores;
        this.stopPolling();
        this.lastBody = body;
        this.setState({
            phase: 'loading', error: null, canUseAi: false, addErrors: [], pollSlow: false, resumed: false, pageUrl: '',
        });
        let response;
        try {
            response = await partStore.photoSearch(id, body);
        } catch (err) {
            response = null;
        }
        if (this.unmounted) {
            return;
        }
        const status = response?.status || 0;
        const data = response?.data || {};
        if (status === 200) {
            this.showCandidates(data);
            return;
        }
        if (status === 202 && data.uuidv4) {
            this.setState({
                mode: 'ai',
                remainingToday: typeof data.remainingToday === 'number' ? data.remainingToday : this.state.remainingToday,
            });
            this.startPolling(data.uuidv4, Date.now());
            return;
        }
        if (status === 409 && data.uuidv4) {
            this.startPolling(data.uuidv4, Date.now());
            return;
        }
        if (status === 429) {
            this.setState({ phase: 'failed', error: 'limit', canUseAi: false });
            return;
        }
        if (status === 409) {
            this.setState({ phase: 'failed', error: 'running', canUseAi: false });
            return;
        }
        this.setState({
            phase: 'failed',
            error: response?.message || 'failed',
            canUseAi: !!data.canUseAi,
        });
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    startPolling = (uuidv4, startedAt) => {
        this.pollStartedAt = startedAt || Date.now();
        this.setState({
            phase: 'pending', uuidv4, error: null, canUseAi: false, pollSlow: false,
        });
        this.schedulePoll(uuidv4);
    }

    schedulePoll = uuidv4 => {
        this.stopPolling();
        this.pollTimer = setTimeout(() => this.poll(uuidv4), POLL_INTERVAL_MS);
    }

    poll = async uuidv4 => {
        const { id } = this.props;
        const { partStore } = this.props.stores;
        if (Date.now() - this.pollStartedAt > POLL_MAX_MS) {
            this.pollTimer = null;
            this.setState({ phase: 'failed', error: 'failed', pollSlow: true });
            return;
        }
        let entry = null;
        try {
            const response = await partStore.photoSearchStatus(id, uuidv4);
            entry = (response?.status === 200 && response.data) ? response.data : null;
        } catch (err) {
            entry = null;
        }
        if (this.unmounted) {
            return;
        }
        if (!entry || entry.status === 'pending') {
            this.schedulePoll(uuidv4);
            return;
        }
        this.pollTimer = null;
        if (this.isStorePart()) {
            const rest = this.storedSearches().filter(e => e && e.uuidv4 !== uuidv4);
            partStore.updateObjectKeyValue('part', 'photoSearches', [...rest, entry]);
        }
        if (entry.status === 'completed') {
            this.showCandidates({ mode: 'ai', ...entry });
            return;
        }
        this.setState({
            phase: 'failed', error: entry.error || 'failed', canUseAi: false, pageUrl: entry.pageUrl || '',
        });
    }

    onSearchAi = e => {
        if (e) e.preventDefault();
        this.search({ force: 'ai' });
    }

    onSearchAgain = e => {
        if (e) e.preventDefault();
        // Repeat the last request as sent. A reused (resumed) result made no
        // request in this drawer; it came from the AI path, so repeat that.
        this.search(this.lastBody || { force: 'ai' });
    }

    onImageError = e => {
        const { url } = e.currentTarget.dataset;
        this.setState(prevState => {
            const selected = { ...prevState.selected };
            delete selected[url];
            return { hidden: { ...prevState.hidden, [url]: true }, selected };
        });
    }

    onToggle = e => {
        const { url } = e.currentTarget.dataset;
        const { hidden } = this.state;
        if (hidden[url]) {
            return;
        }
        this.setState(prevState => {
            const selected = { ...prevState.selected };
            if (selected[url]) {
                delete selected[url];
            } else if (Object.keys(selected).length < MAX_ADD_IMAGES) {
                selected[url] = true;
            }
            return { selected };
        });
    }

    selectedUrls = () => Object.keys(this.state.selected);

    onAdd = async e => {
        if (e) e.preventDefault();
        const { id, drawerLevel = 1, callback = () => {} } = this.props;
        const { appState, partStore } = this.props.stores;
        const { pageUrl, phase } = this.state;
        const urls = this.selectedUrls();
        if (phase === 'adding' || urls.length === 0) {
            return;
        }
        this.setState({ phase: 'adding', addErrors: [] });
        let response;
        try {
            response = await partStore.photoSearchAdd(id, {
                images: urls.map(url => ({ url, sourcePage: pageUrl })),
                pageUrl,
            });
        } catch (err) {
            response = null;
        }
        if (this.unmounted) {
            return;
        }
        const added = response?.data?.added || [];
        const errors = response?.data?.errors || [];
        if (response?.status !== 200 || added.length === 0) {
            this.setState({
                phase: 'candidates',
                addErrors: errors.length > 0 ? errors : [{ url: '', message: response?.message || '' }],
            });
            return;
        }
        if (this.isStorePart()) {
            const old = Array.isArray(partStore.part.images) ? partStore.part.images : [];
            partStore.updateObjectKeyValue('part', 'images', [...old, ...added]);
        }
        appState.addToast({
            type: 'success',
            message: <Text id='part.photo-search-added' fields={{ count: added.length }}>{added.length} photos added.</Text>,
        });
        callback();
        appState.closeDrawer(false, drawerLevel);
    }

    renderCandidates() {
        const {
            candidates, hidden, selected, pageUrl, pageTitle, phase, canUseAi, addErrors, mode, resumed,
        } = this.state;
        const visible = candidates.filter(c => c && c.url && !hidden[c.url]);
        const count = this.selectedUrls().length;
        const adding = phase === 'adding';
        const domain = hostnameOf(pageUrl);
        return (<Fragment>
            {pageUrl && <div class='small text-body-secondary mb-2 part-photo-from'>
                <i class='fa-duotone fa-globe me-1' />
                <Text id='part.photo-search-from' fields={{ domain }}>From {domain}</Text>
                {pageTitle && <Fragment>: <a href={pageUrl} target='_blank' rel='noopener noreferrer'>{pageTitle}</a></Fragment>}
            </div>}
            {visible.length === 0 && <div class='alert alert-info part-photo-none'>
                <i class='fa-duotone fa-image-slash me-2' />
                <Text id='part.photo-search-none'>No photos found.</Text>
            </div>}
            {visible.length > 0 && <div class='row row-cols-3 row-cols-md-4 g-2'>
                {visible.map(c => {
                    const isSelected = !!selected[c.url];
                    return (
                        <div class='col' key={c.url}>
                            <div
                                class={`part-photo-cell position-relative border rounded overflow-hidden bg-body-secondary ${isSelected ? 'border-primary border-3' : ''}`}
                                style='aspect-ratio: 1 / 1; cursor: pointer;'
                                data-url={c.url}
                                onClick={this.onToggle}
                            >
                                <img
                                    class='part-photo-candidate w-100 h-100'
                                    style='object-fit: contain;'
                                    src={c.url}
                                    alt=''
                                    loading='lazy'
                                    referrerpolicy='no-referrer'
                                    data-url={c.url}
                                    onError={this.onImageError}
                                />
                                <span class='badge text-bg-light position-absolute bottom-0 start-0 m-1 small'>
                                    {sourceLabel(c.source)}
                                </span>
                                {isSelected && <span class='position-absolute top-0 end-0 m-1 text-primary'>
                                    <i class='fa-solid fa-circle-check fa-lg' />
                                </span>}
                            </div>
                        </div>
                    );
                })}
            </div>}
            {addErrors.length > 0 && <div class='alert alert-danger part-photo-error mt-3 small'>
                <i class='fa-duotone fa-triangle-exclamation me-2' />
                {addErrors.map(err => <div key={err.url}>{err.url && <span class='text-truncate d-inline-block' style='max-width: 50%;'>{err.url}: </span>}{err.message || <Text id='part.photo-search-failed'>The search failed.</Text>}</div>)}
            </div>}
            <div class='d-flex flex-row align-items-center justify-content-between mt-3'>
                <div>
                    {canUseAi && <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill part-photo-ai' onClick={this.onSearchAi} disabled={adding}>
                        <i class='fa-duotone fa-wand-magic-sparkles me-1' />
                        <Text id='part.photo-search-ai-instead'>Search with AI instead</Text>
                    </button>}
                    {mode === 'ai' && resumed && <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill part-photo-again' onClick={this.onSearchAgain} disabled={adding}>
                        <i class='fa-duotone fa-rotate me-1' />
                        <Text id='part.photo-search-again'>Search again</Text>
                    </button>}
                </div>
                <button type='button' class='btn btn-primary rounded-pill part-photo-add' onClick={this.onAdd} disabled={adding || count === 0}>
                    <i class={`fa-duotone ${adding ? 'fa-spinner fa-spin' : 'fa-plus'} me-2`} />
                    <Text id='part.photo-search-add' fields={{ count }}>Add ({count})</Text>
                </button>
            </div>
        </Fragment>);
    }

    renderFailed() {
        const {
            error, canUseAi, pollSlow, pageUrl,
        } = this.state;
        if (error === 'limit') {
            return (
                <div class='alert alert-warning part-photo-limit'>
                    <i class='fa-duotone fa-hourglass-half me-2' />
                    <Text id='part.photo-search-limit'>The daily limit for AI searches is reached.</Text>
                </div>
            );
        }
        if (error === 'running') {
            return (
                <div class='alert alert-info part-photo-running'>
                    <i class='fa-duotone fa-spinner me-2' />
                    <Text id='part.photo-search-running'>A search is already running.</Text>
                </div>
            );
        }
        return (<Fragment>
            <div class='alert alert-danger part-photo-error'>
                <i class='fa-duotone fa-triangle-exclamation me-2' />
                {error && error !== 'failed' ? error : <Text id='part.photo-search-failed'>The search failed.</Text>}
                {pollSlow && <span class='ms-1'><Text id='part.ai-lookup-slow'>This is taking longer than usual. The answer will show up here when it is ready.</Text></span>}
                {pageUrl && <div class='small mt-1 text-truncate'>
                    <i class='fa-duotone fa-arrow-up-right-from-square me-1' />
                    <a href={pageUrl} target='_blank' rel='noopener noreferrer'>{pageUrl}</a>
                </div>}
            </div>
            <div class='d-flex flex-row gap-2'>
                {canUseAi && <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill part-photo-ai' onClick={this.onSearchAi}>
                    <i class='fa-duotone fa-wand-magic-sparkles me-1' />
                    <Text id='part.photo-search-ai-instead'>Search with AI instead</Text>
                </button>}
                {!canUseAi && <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill part-photo-again' onClick={this.onSearchAgain}>
                    <i class='fa-duotone fa-rotate me-1' />
                    <Text id='part.photo-search-again'>Search again</Text>
                </button>}
            </div>
        </Fragment>);
    }

    render() {
        const { title } = this.props;
        const { phase, remainingToday } = this.state;
        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header d-flex flex-row align-items-center'>
                        <i class='fa-regular fa-images me-2' />
                        <Text id='part.photo-search-title'>Find product photos</Text>
                        {title && <span class='ms-2 text-body-secondary'>- {title}</span>}
                    </div>

                    <div class='px-3 mt-3'>
                        {phase === 'loading' && <div class='text-body-secondary'>
                            <i class='fa-duotone fa-spinner fa-spin me-2' />
                            <Text id='part.ai-lookup-searching'>Searching the web...</Text>
                        </div>}
                        {phase === 'pending' && <div class='alert alert-info part-photo-pending'>
                            <i class='fa-duotone fa-spinner fa-spin me-2' />
                            <Text id='part.photo-search-pending'>The AI is looking for the manufacturer's page. This can take a minute.</Text>
                            {typeof remainingToday === 'number' && <small class='d-block text-body-secondary mt-1'>
                                <Text id='part.ai-lookup-remaining' fields={{ count: remainingToday }}>{remainingToday} lookups left today</Text>
                            </small>}
                        </div>}
                        {(phase === 'candidates' || phase === 'adding') && this.renderCandidates()}
                        {phase === 'failed' && this.renderFailed()}
                        <HelpText>
                            <Text id='part.photo-search-help'>Photos are taken from the product page. Pick one or more and press «Add».</Text>
                        </HelpText>
                    </div>
                </div>
            </div>
        );
    }
}

export default PartPhotoSearch;
